<template>
  <div class="submission-details">
    <!-- Configurator Actions -->
    <div v-if="isConfiguratorSubmission" class="submission-details__actions">
      <div class="actions-header">
        <span class="configurator-badge">
          <BaseIcon name="zap" size="14" />
          Configurator
        </span>
        <span v-if="hasLinkedProposal" class="proposal-badge">
          <BaseIcon name="file-text" size="14" />
          Offerte aangemaakt
        </span>
      </div>
      <div class="actions-buttons">
        <Btn
          v-if="!hasSuccessorProposal"
          variant="secondary"
          size="sm"
          icon="edit"
          @click="handleReviseConfiguration()"
          :loading="revising"
        >
          Wijzig configuratie
        </Btn>
        <Btn
          v-if="canMakeDefinitive"
          variant="primary"
          size="sm"
          icon="ruler"
          @click="handleReviseConfiguration('create_definitive_from_proforma')"
          :loading="revising"
        >
          {{ $t('proposals.make_definitive') }}
        </Btn>
        <Btn
          v-if="canCreateProposal"
          variant="primary"
          size="sm"
          icon="file-text"
          @click="handleCreateProposal"
          :loading="creatingProposal"
        >
          Maak offerte
        </Btn>
        <Btn
          v-if="hasLinkedProposal && linkedProposalId"
          variant="primary"
          size="sm"
          icon="file-text"
          @click="navigateToProposal"
        >
          Naar offerte
        </Btn>
      </div>
    </div>

    <!-- Lead Card -->
    <div class="submission-details__lead" v-if="leadData">
      <LeadCard :lead="leadData" />
    </div>

    <div class="submission-details__metadata">
      <div class="metadata-item">
        <label>{{ $t('submissions.last_updated') }}</label>
        <p>{{ formatDate(submission.updated_at) }}</p>
      </div>
    </div>

    <div class="submission-details__data">
      <h3>{{ $t('submissions.form_data') }}</h3>
      <div class="submission-details__fields">
        <div v-for="answer in getProcessedAnswers()" :key="answer.fieldName" class="field-item">
          <label>{{ answer.fieldName }}</label>
          <div v-if="Array.isArray(answer.value)">
            <p v-for="value in answer.value" :key="value">{{ value }}</p>
          </div>
          <div v-else>{{ answer.value }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, getCurrentInstance, ref, computed } from 'vue';
  import { ELEMENTS } from '@/constants/elements';
  import { formatDate } from '@/utils/date';
  import { useToastStore } from '@/store/toastStore';
  import { crmService } from '@/services/crmService';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LeadCard from '@/components/partials/Lead/LeadCard.vue';

  export default defineComponent({
    name: 'SubmissionDetails',

    components: {
      Btn,
      BaseIcon,
      LeadCard,
    },

    props: {
      submission: {
        type: Object,
        required: true,
      },
      form: {
        type: Object,
        required: true,
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const toastStore = useToastStore();
      console.log(props.submission);

      const revising = ref(false);
      const creatingProposal = ref(false);

      // Check if this is a configurator submission
      const isConfiguratorSubmission = computed(() => {
        const result =
          props.submission.type === 'configurator' || props.form?.type === 'configurator';
        return result;
      });

      // Check if submission already has a linked proposal
      const hasLinkedProposal = computed(() => {
        return props.submission.proposals?.length > 0;
      });

      // Fallback when auto-create failed or is disabled: always allow manual
      // create for configurator submissions that still have no linked proposal.
      const canCreateProposal = computed(() => {
        if (!isConfiguratorSubmission.value) return false;
        if (hasLinkedProposal.value) return false;
        return true;
      });

      // Get linked proposal (full object) for navigation and status checks
      const linkedProposal = computed(() => {
        if (props.submission.proposals?.length > 0) {
          return props.submission.proposals[0];
        }
        return null;
      });

      const linkedProposalId = computed(() => linkedProposal.value?.id || null);

      const hasSuccessorProposal = computed(() => {
        return !!linkedProposal.value?.successor_proposal;
      });

      // Show "Definitief maken" only when the linked proposal is a proforma
      // that is still actionable (draft/sent/viewed).
      const canMakeDefinitive = computed(() => {
        const p = linkedProposal.value;
        if (hasSuccessorProposal.value) return false;
        if (!p || p.proposal_type !== 'proforma') return false;
        return p.status === 'draft' || p.status === 'sent' || p.status === 'viewed';
      });

      const isProposalHasSuccessorError = (error) => {
        const data = error?.response?.data || {};
        return (
          error?.response?.status === 409 &&
          (data.code === 'PROPOSAL_HAS_SUCCESSOR' || data.error === 'PROPOSAL_HAS_SUCCESSOR')
        );
      };

      // Get lead data (try both lead and leads for backwards compatibility)
      const leadData = computed(() => {
        return props.submission.lead || props.submission.leads || null;
      });

      // Navigate to the linked proposal
      const navigateToProposal = () => {
        if (linkedProposalId.value) {
          window.location.href = `/proposals?open=${linkedProposalId.value}`;
        }
      };

      // Handle revise configuration. The optional intent is forwarded to the
      // service so the backend bakes it into the returned revise_url
      // (e.g. intent=create_definitive_from_proforma).
      const handleReviseConfiguration = async (intent = null) => {
        if (hasSuccessorProposal.value) return;
        try {
          revising.value = true;

          // Build return URL - will redirect here after revision (use linked proposal or proposals list)
          const returnUrl = linkedProposalId.value
            ? `${window.location.origin}/proposals?open=${linkedProposalId.value}`
            : `${window.location.origin}/proposals`;

          const response = await crmService.getSubmissionReviseUrl(
            props.submission.id,
            returnUrl,
            intent
          );

          if (response.revise_url) {
            window.open(response.revise_url, '_blank');
          } else {
            throw new Error('No revise URL returned');
          }
        } catch (error) {
          console.error('Failed to get revise URL:', error);
          toastStore.addToast({
            message: isProposalHasSuccessorError(error)
              ? $t('proposals.revise_has_successor_error')
              : $t('proposals.revise_configuration_error'),
            type: 'error',
          });
        } finally {
          revising.value = false;
        }
      };

      // Handle create proposal manually
      const handleCreateProposal = async () => {
        try {
          creatingProposal.value = true;

          const response = await crmService.createProposalFromSubmission(props.submission.id);

          if (response.proposal) {
            toastStore.addToast({
              message: 'Offerte succesvol aangemaakt',
              type: 'success',
            });

            // Navigate to proposals page with query param to open modal
            window.location.href = `/proposals?open=${response.proposal.id}`;
          }
        } catch (error) {
          console.error('Failed to create proposal:', error);
          toastStore.addToast({
            message: 'Kon offerte niet aanmaken',
            type: 'error',
          });
        } finally {
          creatingProposal.value = false;
        }
      };

      // Format field value helper
      const formatFieldValue = (field) => {
        const value = field.value;

        // Handle address object - check both by type and by structure
        if (
          (field.type === 'address' ||
            (typeof value === 'object' &&
              value !== null &&
              'street' in value &&
              'city' in value)) &&
          typeof value === 'object' &&
          value !== null
        ) {
          const addr = value;
          return [addr.street, addr.houseNumber, addr.postalCode, addr.city]
            .filter(Boolean)
            .join(', ');
        }

        // Handle name object - show full_name
        if (typeof value === 'object' && value !== null && 'full_name' in value) {
          return value.full_name || '';
        }

        // Handle other objects - stringify as fallback
        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value);
        }

        return value || '';
      };

      // Get field label helper
      const getFieldLabel = (fieldId) => {
        const labels = {
          'name-input': 'Naam',
          'email-input': 'E-mail',
          'phone-input': 'Telefoon',
          'address-input': 'Adres',
        };
        return labels[fieldId] || fieldId;
      };

      // Format product display for enriched data
      const formatProductDisplay = (product) => {
        const parts = [product.productName];

        if (product.options && product.options.length > 0) {
          const options = product.options
            .filter((opt) => opt.showOnQuote !== false)
            .map((opt) => `${opt.optionLabel}: ${opt.displayValue}`)
            .join(', ');
          if (options) parts.push(`(${options})`);
        }

        if (product.pricing?.lineTotal) {
          parts.push(
            `- €${product.pricing.lineTotal.toLocaleString('nl-NL', {
              minimumFractionDigits: 2,
            })}`
          );
        }

        return parts.join(' ');
      };

      // Process enriched data for configurator submissions
      const processEnrichedData = (enriched) => {
        const results = [];

        // Add contact info from submission data
        const submissionData = props.submission.data || {};
        const contactFields = ['name-input', 'email-input', 'phone-input', 'address-input'];

        contactFields.forEach((fieldId) => {
          let field = null;
          let fieldValue = null;

          // Try to find field in various data structures
          // 1. Check if data is an array of objects with fieldId
          if (Array.isArray(submissionData)) {
            field = submissionData.find((f) => f.fieldId === fieldId);
            if (field) {
              fieldValue = field.value;
            }
          } else {
            // 2. Check if data is an object with numeric keys (like {"0": {...}, "1": {...}})
            const allValues = Object.values(submissionData);
            field = allValues.find(
              (f) =>
                (typeof f === 'object' && f !== null && f.fieldId === fieldId) ||
                (typeof f === 'object' && f !== null && 'fieldId' in f && f.fieldId === fieldId)
            );

            if (field) {
              // Keep empty strings. `||` treated "" as missing and showed the field object.
              fieldValue = Object.prototype.hasOwnProperty.call(field, 'value')
                ? field.value
                : field;
            } else if (submissionData[fieldId]) {
              // 3. Direct access by fieldId key
              fieldValue = submissionData[fieldId];
            }
          }

          if (fieldValue !== null && fieldValue !== undefined) {
            // Determine field type based on fieldId or value structure
            let fieldType = field && field.type;

            if (!fieldType) {
              if (fieldId === 'address-input') {
                fieldType = 'address';
              } else if (
                typeof fieldValue === 'object' &&
                fieldValue !== null &&
                'street' in fieldValue &&
                'city' in fieldValue
              ) {
                // Auto-detect address by structure
                fieldType = 'address';
              } else if (
                typeof fieldValue === 'object' &&
                fieldValue !== null &&
                'full_name' in fieldValue
              ) {
                // Auto-detect name by structure
                fieldType = 'name';
              } else {
                fieldType = 'text';
              }
            }

            results.push({
              fieldName: getFieldLabel(fieldId),
              type: fieldType,
              value: formatFieldValue({ type: fieldType, value: fieldValue }),
            });
          }
        });

        // Add products from enriched data
        if (enriched.products && enriched.products.length > 0) {
          enriched.products.forEach((product, index) => {
            results.push({
              fieldName: `Product ${index + 1}: ${product.productName}`,
              type: 'configurator-product',
              value: formatProductDisplay(product),
            });
          });
        }

        // Add totals
        if (enriched.totals) {
          results.push({
            fieldName: 'Totaalprijs',
            type: 'price',
            value: `€${enriched.totals.total.toLocaleString('nl-NL', {
              minimumFractionDigits: 2,
            })}`,
          });
        }

        return results;
      };

      const getProcessedAnswers = () => {
        // Check if this is a configurator submission with enriched data
        const isConfigurator =
          props.submission.type === 'configurator' || props.form?.type === 'configurator';
        const enrichedData = props.submission.data?._enriched;

        if (isConfigurator && enrichedData) {
          // Use enriched data for configurator submissions
          return processEnrichedData(enrichedData);
        }

        // Fallback to normal processed data
        const processedData = props.submission.metadata?.processed_data || [];
        return processedData
          .filter((field) => {
            // Find the element definition to get the category
            const elementDef = ELEMENTS.find((e) => e.type === field.type);
            return elementDef && elementDef.category !== 'content';
          })
          .map((field) => {
            let processedValue = field.formattedValue;

            // Check if value is empty, null, or empty array
            if (
              !processedValue ||
              (Array.isArray(processedValue) && processedValue.length === 0) ||
              processedValue === null
            ) {
              processedValue = $t('submissions.no_value_provided');
            } else {
              // Handle address formatting
              if (field.type === 'address' && typeof field.formattedValue === 'object') {
                const address = field.formattedValue;
                const addressParts = [
                  address.street,
                  address.houseNumber,
                  address.postalCode,
                  address.city,
                  address.state,
                  address.country,
                ].filter((part) => part && part.trim());

                processedValue =
                  addressParts.length > 0
                    ? addressParts.join(', ')
                    : $t('submissions.no_value_provided');
              }

              // Handle file upload / photo-drawing - show URL
              if (
                (field.type === 'file-upload' || field.type === 'photo-drawing') &&
                field.formattedValue?.url
              ) {
                processedValue = field.formattedValue.url;
              }
            }

            return {
              fieldName: field.fieldName,
              type: field.type,
              value: processedValue,
            };
          });
      };

      return {
        formatDate,
        getProcessedAnswers,
        isConfiguratorSubmission,
        hasLinkedProposal,
        canCreateProposal,
        linkedProposalId,
        hasSuccessorProposal,
        canMakeDefinitive,
        leadData,
        revising,
        creatingProposal,
        handleReviseConfiguration,
        handleCreateProposal,
        navigateToProposal,
      };
    },
  });
</script>

<style scoped lang="scss">
  .submission-details {
    padding: 1rem;

    &__actions {
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1.5rem;

      .actions-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 1rem;
      }

      .configurator-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        padding: 0.25rem 0.625rem;
        background: var(--purple-100);
        color: var(--purple-700);
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
      }

      .proposal-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        padding: 0.25rem 0.625rem;
        background: var(--green-100);
        color: var(--green-700);
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
      }

      .actions-buttons {
        display: flex;
        gap: 0.75rem;
        flex-wrap: wrap;
      }
    }

    &__lead {
      margin-bottom: 1.5rem;
    }

    &__metadata {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;
      margin-bottom: 2rem;

      .metadata-item {
        label {
          font-size: 0.875rem;
          color: var(--slate-600);
          margin-bottom: 0.25rem;
        }

        p {
          font-size: 1rem;
          color: var(--slate-900);
          margin: 0;
        }
      }
    }

    &__data {
      h3 {
        font-size: 1.125rem;
        margin-bottom: 1rem;
      }

      .data-preview {
        background: var(--slate-100);
        border-radius: 0.5rem;
        padding: 1rem;
        overflow: auto;
        max-height: 400px;

        pre {
          margin: 0;
          font-family: monospace;
          font-size: 0.875rem;
        }
      }
    }
    &__fields {
      .field-item {
        padding: 0.5rem 0;
        border-top: 1px solid var(--slate-200);

        label {
          font-size: 0.875rem;
          color: var(--slate-800);
          margin-bottom: 0.25rem;
          font-weight: 600;
        }
      }
    }
  }
</style>
